import React from 'react';
import { Box, MenuItem, MenuList, Popover, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { MiniGameId } from './types';
import { IMiniGameEntry } from './catalog';
import { GameGlyph } from './gameIcons';
import { getLauncherStyles } from './launcherStyles';
import {
  MINI_GAMES_FOOTER,
  MINI_GAMES_KICKER,
  MINI_GAMES_PICKER_ROOM_PX,
  MINI_GAMES_PLAY,
} from './constants';

export const PICKER_ID = 'myta-cinema-minigames-picker';

export const opensUpward = (
  anchor: { top: number; bottom: number },
  viewportHeight: number,
): boolean => {
  const below = viewportHeight - anchor.bottom;

  return below < MINI_GAMES_PICKER_ROOM_PX && anchor.top > below;
};

interface IGamePickerProps {
  anchor: HTMLElement | null;
  up: boolean;
  classes: ReturnType<typeof getLauncherStyles>;
  accent: string;
  games: IMiniGameEntry[];
  percent: number;
  statusLine: string | null;
  onPick: (id: MiniGameId) => void;
  onDismiss: (returnFocus: boolean) => void;
  onExited: () => void;
}

const GamePicker = ({
  anchor,
  up,
  classes,
  accent,
  games,
  percent,
  statusLine,
  onPick,
  onDismiss,
  onExited,
}: IGamePickerProps) => {
  const leaveOnTab = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      onDismiss(true);
    }
  };

  return (
    <Popover
      open={!!anchor}
      anchorEl={anchor}
      onClose={(_event, reason) => onDismiss(reason === 'escapeKeyDown')}
      anchorOrigin={{ vertical: up ? 'top' : 'bottom', horizontal: 'center' }}
      transformOrigin={{
        vertical: up ? 'bottom' : 'top',
        horizontal: 'center',
      }}
      disableRestoreFocus
      disableScrollLock
      TransitionProps={{ onExited }}
      PaperProps={{
        sx: [classes.paper, up ? classes.paperUp : classes.paperDown],
      }}
    >
      <Box data-testid="myta-cinema-minigames-picker">
        <Box sx={classes.head}>
          <Typography component="span" sx={classes.kicker}>
            {MINI_GAMES_KICKER}
          </Typography>
          <Box sx={classes.status}>
            <Box sx={classes.dot} />
            <Typography
              component="span"
              sx={classes.eta}
              data-testid="myta-cinema-minigames-eta"
            >
              {statusLine}
            </Typography>
            <Typography component="span" sx={classes.pct}>
              {percent}%
            </Typography>
          </Box>
        </Box>
        <MenuList
          id={PICKER_ID}
          autoFocusItem
          aria-label={MINI_GAMES_PLAY}
          sx={classes.list}
          onKeyDown={leaveOnTab}
        >
          {games.map((game) => (
            <MenuItem
              key={game.id}
              disableRipple
              onClick={() => onPick(game.id)}
              sx={classes.item}
              data-testid={`myta-cinema-minigame-${game.id}`}
            >
              <Box className="myta-mg-tile" sx={classes.tile}>
                <GameGlyph id={game.id} accent={accent} />
              </Box>
              <Box sx={classes.itemText}>
                <Box sx={classes.itemTitleRow}>
                  <Typography component="span" sx={classes.itemTitle}>
                    {game.title}
                  </Typography>
                  {game.badge ? (
                    <Typography component="span" sx={classes.itemBadge}>
                      {game.badge}
                    </Typography>
                  ) : null}
                </Box>
                <Typography component="span" sx={classes.itemBlurb}>
                  {game.blurb}
                </Typography>
              </Box>
              <ArrowForwardIcon className="myta-mg-go" sx={classes.itemGo} />
            </MenuItem>
          ))}
        </MenuList>
        <Typography component="span" sx={classes.foot}>
          {MINI_GAMES_FOOTER}
        </Typography>
      </Box>
    </Popover>
  );
};

export default GamePicker;
